import React, {useState, useEffect, useRef} from "react"
import styled from "styled-components"
import moment from "moment-timezone"
import {useAuth0} from "@auth0/auth0-react"
// Assets
import CalendarIconPurple from "assets/images/png/icon-bookings@3x.png"
// UI
import {Container, Row, Col} from "@uprise/grid"
import {Modal} from "@uprise/modal"
import {TextInputHorizontal} from "@uprise/form"
import {H4, H6} from "@uprise/headings"
import {Button} from "@uprise/button"
import {extended, semantic, primary} from "@uprise/colors"
// redux
import {
	bookingDataSelector,
	createBooking,
	fetchCoachAvailability,
	clearBookingSlot,
	cancelBooking
} from "features/Bookings/bookingSlice"
import {useDispatch, useSelector} from "react-redux"
import {useForm, Controller} from "react-hook-form"
// style
import {ModalButtonContainer} from "features/Bookings/AllBookingsTable/index.style"
// constants
import {issuesDropdown} from "constants/issues"
// Components
import Calendar from "./Calendar"
import DropDown from "components/shared/DropDown"

const FormElementContainer = styled.div`
	width: 100%;
	display: flex;
	justify-content: space-between;
	align-items: center;
	border-bottom: ${props => (props.error ? "1px solid #ff9999" : "1px solid #edeafa")};
`

const FormElementLabel = styled(H6)`
	margin: 16px 0 8px 0;

	&:after {
		content: "${props => (props.isRequired ? "*" : "")}";
	}
`

const FormInputContainer = styled.div`
	flex-basis: 60%;
	position: relative;
	align-self: center;
`

const FormTextInputWithBorder = styled(TextInputHorizontal)`
	border-radius: 4px;
	border: solid 1px #d4cbfb;
	padding: 4px 10px;
`

const ButtonContainer = styled.div`
	display: flex;
`

const PrimaryButton = styled(Button)`
	width: 100%;
	min-width: 100px;
	height: 50px;
	margin: 10px 6px 0;
	outline: none;
`

const MessageStyles = styled.label`
	font-size: 12px;
	color: ${props => {
		if (props.focused) {
			return `${extended.blue.one}`
		} else if (props.validation) {
			return `${semantic.error}`
		} else {
			return `${extended.purple.five}`
		}
	}};
	display: block;
	margin-top: 8px;
	transition: font-size 0.2s;
	padding-left: 40%;
	text-align: left;
`

const MessageStylesCaps = styled(MessageStyles)`
	padding-left: 40%;
	text-align: left;
`

const ReadOnlyField = styled.div`
	color: #6d6c79;
	text-align: left;
	width: 60%;
`

const ReadOnlyFieldHighlighted = styled(ReadOnlyField)`
	color: ${primary.purple};
`

const NewBooking = ({onCancel, user, coach, coaches, authData, booking, reschedule}) => {
	const dispatch = useDispatch()
	const {getAccessTokenSilently} = useAuth0()

	const {register, control, handleSubmit, watch, setValue, errors} = useForm({
		// defaultValues: {}; you can populate the fields by this attribute
		mode: "onSubmit",
		reValidateMode: "onChange"
	})

	const [calendarModal, setCalendarModal] = useState(false)
	const [coachesDropdown, setCoachesDropdown] = useState(coaches.map(coach => ({label: coach.name, value: coach.id})))
	const [selectedCoach, setSelectedCoach] = useState(coachesDropdown.find(coach => coach.value === authData.id))
	const [selectedDate, setSelectedDate] = useState(moment())
	const [duration, setDuration] = useState(30)
	const [timezone, setTimezone] = useState()
	const [loading, setLoading] = useState(false)

	const {coachAvailability, bookingSlot} = useSelector(bookingDataSelector)

	useEffect(() => {
		const coachesList = coaches.map(coach => ({label: coach.name, value: coach.id}))
		setCoachesDropdown(coachesList)
		setSelectedCoach(coachesList.find(coach => coach.value === authData.id)?.value)
		dispatch(clearBookingSlot())
	}, [])

	useEffect(() => {
		setTimezone(coaches.find(coach => coach.id === selectedCoach)?.data?.timezone || "Australia/Sydney")
		dispatch(clearBookingSlot())
	}, [selectedCoach])

	useEffect(() => {
		getBookingsData()
	}, [timezone, selectedDate, duration, selectedCoach])

	useEffect(() => {
		setValue("bookingSlot", bookingSlot)
	}, [bookingSlot])

	const {name, phone, email, countryCode, employerCode, countryName} = user

	const token = useRef(null)

	const getToken = () => {
		if (!token.current) {
			return new Promise(async (res, rej) => {
				try {
					const authToken = await getAccessTokenSilently()
					token.current = authToken
					res(authToken)
				} catch (err) {
					console.log(err)
					rej(err)
				}
			})
		} else return Promise.resolve()
	}

	const handleCancelBooking = value => {
		getToken().then(() => {
			setLoading(true)
			dispatch(
				cancelBooking({
					coachId: booking.coachId,
					eventId: booking.eventId,
					bookingId: booking.id,
					userEmail: booking.email,
					cancelReason: "inTime",
					token: token.current,
					status: true
				})
			).then(() => {
				handleCreateBooking(value)
			})
		})
	}

	const handleCreateBooking = value => {
		const data = {
			name: name,
			phone: phone,
			countryCode: countryCode,
			countryName,
			employerCode: employerCode,
			clientEmail: email,
			coachId: value.coach,
			purpose: "Therapy",
			startTime: value.bookingSlot.start,
			callDuration: duration,
			timeZone: timezone,
			issues: enableOthers ? value.other : value.issues
		}

		getToken().then(() => {
			setLoading(true)
			dispatch(createBooking({data, token: token.current}))
				.then(() => {
					dispatch(clearBookingSlot())
					setCalendarModal(false)
					onCancel()
				})
				.finally(() => setLoading(false))
		})
	}

	const getBookingsData = () => {
		getToken().then(() => {
			const coachSelected = coaches.find(coach => coach.id === selectedCoach)
			if (coachSelected)
				dispatch(
					fetchCoachAvailability({
						data: {
							duration,
							email: coachSelected.email,
							timezone,
							week: selectedDate.isoWeek()
						},
						token: token.current
					})
				)
		})
	}

	const enableOthers = watch("issues") === "Others"

	const validateText = text => {
		return !text.trim() ? `Field can not be left empty` : true
	}

	const isCoachEnabled = coachesDropdown.some(coach => coach.value === selectedCoach)

	return (
		<Container>
			<form
				onSubmit={handleSubmit(formData => {
					if (reschedule) handleCancelBooking(formData)
					else handleCreateBooking(formData)
				})}>
				<Row>
					{/* =================== Booking ====================== */}
					<Col>
						<H4 style={{textAlign: "left"}} className='m-t-10 m-b-5'>
							Booking Details
						</H4>
					</Col>

					<Col className='col-6 col-lg-6 col-md-6 col-sm-12'>
						<FormElementContainer>
							<FormElementLabel>Coach</FormElementLabel>
							<FormInputContainer>
								<Controller
									name={"coach"}
									control={control}
									defaultValue={authData.id}
									rules={{
										required: {
											value: true,
											message: "Please select a coach"
										}
									}}
									render={props => (
										<DropDown
											items={coachesDropdown}
											onChange={e => {
												setSelectedCoach(e.value)
												props.onChange(e.value)
											}}
											label='Any Coach'
											menuStyle={{position: "absolute", zIndex: 2}}
											variant={"white"}
											initialSelectedItem={selectedCoach}
											isLast={true}
											borderRadius={"4px"}
										/>
									)}
								/>
							</FormInputContainer>
						</FormElementContainer>

						{errors["coach"] && errors["coach"].message ? (
							<MessageStyles htmlFor={1} focused={false} validation={errors["coach"]}>
								{errors["coach"] && errors["coach"].message}
							</MessageStyles>
						) : null}
					</Col>

					<Col className='col-6 col-lg-6 col-md-6 col-sm-12'>
						<FormElementContainer>
							<FormElementLabel>Issues to be Discussed</FormElementLabel>
							<FormInputContainer>
								<Controller
									name={"issues"}
									control={control}
									rules={{
										required: {
											value: false,
											message: "Please select a value"
										}
									}}
									render={props => (
										<DropDown
											isFirst={false}
											isLast={true}
											items={issuesDropdown.map(issue => ({label: issue, value: issue}))}
											label='Select Item'
											variant='primary'
											onChange={item => props.onChange(item.value)}
											menuStyle={{position: "absolute", zIndex: 2}}
											sort={false}
										/>
									)}
								/>
							</FormInputContainer>
						</FormElementContainer>

						{errors["issues"] && errors["issues"].message ? (
							<MessageStyles htmlFor={1} focused={false} validation={errors["issues"]}>
								{errors["issues"] && errors["issues"].message}
							</MessageStyles>
						) : null}
					</Col>

					<Col className='col-6 col-lg-6 col-md-6 col-sm-12'>
						<FormElementContainer>
							<FormElementLabel>Booking Date/Time*</FormElementLabel>
							<ReadOnlyFieldHighlighted onClick={() => setCalendarModal(isCoachEnabled)}>
								<Controller
									name={"bookingSlot"}
									control={control}
									rules={{
										required: {
											value: true,
											message: "Booking slot is Required"
										}
									}}
									as={
										<>
											{bookingSlot ? (
												<Row>
													<Col>
														{moment(bookingSlot.start)
															.tz(timezone)
															.format("DD-MMM-YYYY (dddd)")}
													</Col>
													<Col>
														{moment(bookingSlot.start).tz(timezone).format("hh:mm A z")}
													</Col>
												</Row>
											) : (
												<Row className='justify-content-start'>
													<Col className='col-12 col-md-12'>
														Select Date/Time
														<img
															style={{width: 16, marginLeft: 12}}
															alt='calendar'
															src={CalendarIconPurple}
														/>
													</Col>
												</Row>
											)}
										</>
									}
								/>
							</ReadOnlyFieldHighlighted>
						</FormElementContainer>

						{errors["bookingSlot"] && errors["bookingSlot"].message ? (
							<MessageStyles htmlFor={1} focused={false} validation={errors["bookingSlot"]}>
								{errors["bookingSlot"] && errors["bookingSlot"].message}
							</MessageStyles>
						) : null}
					</Col>

					{enableOthers && (
						<Col className='col-6 col-lg-6 col-md-6 col-sm-12'>
							<FormElementContainer>
								<FormElementLabel>Others*</FormElementLabel>
								<FormInputContainer>
									<Controller
										name={"other"}
										control={control}
										rules={{
											required: {
												value: enableOthers,
												message: "Field can not be left empty"
											},
											validate: text => {
												if (!enableOthers) return true
												else return validateText(text)
											},
											maxLength: {
												value: 500,
												message: "Field can not exceed 500 characters"
											}
										}}
										as={
											<FormTextInputWithBorder
												type='text'
												ref={register({required: true})}
												disabled={!enableOthers}
											/>
										}
									/>
								</FormInputContainer>
							</FormElementContainer>

							{errors["other"] && errors["other"].message ? (
								<MessageStyles htmlFor={1} focused={false} validation={errors["other"]}>
									{errors["other"] && errors["other"].message}
								</MessageStyles>
							) : null}
						</Col>
					)}
				</Row>

				<Row>
					{/* =================== user ====================== */}
					<Col>
						<H4 style={{textAlign: "left"}} className='m-t-10 m-b-5'>
							User Details
						</H4>
					</Col>
					<Col className='col-6 col-lg-6 col-md-6 col-sm-12'>
						<FormElementContainer>
							<FormElementLabel>Name</FormElementLabel>
							<FormInputContainer>
								<ReadOnlyField>{name}</ReadOnlyField>
							</FormInputContainer>
						</FormElementContainer>
					</Col>
					<Col className='col-6 col-lg-6 col-md-6 col-sm-12'>
						<FormElementContainer>
							<FormElementLabel>Phone</FormElementLabel>
							<FormInputContainer>
								<ReadOnlyField>{`${countryCode} ${phone}`}</ReadOnlyField>
							</FormInputContainer>
						</FormElementContainer>
					</Col>
					<Col className='col-6 col-lg-6 col-md-6 col-sm-12'>
						<FormElementContainer>
							<FormElementLabel>Email</FormElementLabel>
							<FormInputContainer>
								<ReadOnlyField>{email}</ReadOnlyField>
							</FormInputContainer>
						</FormElementContainer>
					</Col>
					<Col className='col-6 col-lg-6 col-md-6 col-sm-12'>
						<FormElementContainer>
							<FormElementLabel>Access Code</FormElementLabel>
							<FormInputContainer>
								<ReadOnlyField>{employerCode}</ReadOnlyField>
							</FormInputContainer>
						</FormElementContainer>
					</Col>
				</Row>

				<Row className='flex-row-reverse'>
					<ButtonContainer>
						<PrimaryButton type='submit' variant={"tertiary"} title={"Cancel"} onClick={onCancel} />
						<PrimaryButton type='submit' variant={"primary"} title={"Save"} isLoading={loading} />
					</ButtonContainer>
				</Row>
			</form>
			<Modal
				isOpen={calendarModal}
				handleClose={() => {
					dispatch(clearBookingSlot())
					setCalendarModal(false)
				}}
				padding={"24px"}
				backgroundColor={"#ffffff"}
				width={"70%"}
				scroll={true}
				textAlign='center'>
				<H4>{`Availability for ${coaches.find(coach => coach.id === selectedCoach)?.name}`}</H4>
				<ModalButtonContainer>
					<Calendar
						coachAvailability={coachAvailability}
						selectedCoach={selectedCoach}
						timezone={timezone}
						selectedDate={selectedDate}
						setSelectedDate={setSelectedDate}
						coach={coaches.find(coach => coach.id === selectedCoach)}
						setTimezone={setTimezone}
						setDuration={setDuration}
						setCalendarModal={setCalendarModal}
					/>
				</ModalButtonContainer>
			</Modal>
		</Container>
	)
}

export default NewBooking
